import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ClientCommand, PublicRetroRoom, PublicRoom, Role, ServerMessage } from '@planning-session/shared';
import { socketUrl } from './api.js';
import { seatToken } from './storage.js';

export type ConnectionStatus = 'connecting' | 'live' | 'reconnecting' | 'rejected';

export interface SessionCredentials {
  readonly roomId: string;
  readonly name: string;
  readonly role: Role;
}

export interface Session {
  readonly status: ConnectionStatus;
  readonly room: PublicRoom | PublicRetroRoom | null;
  /** Last rejection from the server, for a transient notice in the UI. */
  readonly notice: string | null;
  readonly fatal: string | null;
  send: (command: ClientCommand) => void;
  leave: () => void;
  dismissNotice: () => void;
}

const RECONNECT_DELAYS_MS = [500, 1_000, 2_000, 4_000, 8_000];
const NOTICE_TIMEOUT_MS = 4_000;
/** Errors that mean reconnecting will not help. */
const FATAL_CODES = new Set(['room_not_found', 'room_full', 'invalid_name']);

/**
 * Owns the websocket for one seat at one table: says hello, keeps the latest
 * room projection, and re-announces itself with the stored seat token whenever
 * the connection drops.
 */
export function useSession(credentials: SessionCredentials | null): Session {
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const [room, setRoom] = useState<PublicRoom | PublicRetroRoom | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [fatal, setFatal] = useState<string | null>(null);

  const socketRef = useRef<WebSocket | null>(null);
  const attemptRef = useRef(0);
  const retryTimerRef = useRef<number | null>(null);
  const closedByUsRef = useRef(false);

  const { roomId, name, role } = credentials ?? { roomId: '', name: '', role: 'player' as Role };

  useEffect(() => {
    if (!credentials) return;

    closedByUsRef.current = false;
    let disposed = false;

    const clearRetry = () => {
      if (retryTimerRef.current !== null) {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };

    const connect = () => {
      if (disposed) return;

      const socket = new WebSocket(socketUrl());
      socketRef.current = socket;

      socket.addEventListener('open', () => {
        if (disposed) return;
        attemptRef.current = 0;
        const token = seatToken.get(roomId);
        socket.send(JSON.stringify({ type: 'hello', roomId, name, role, ...(token ? { token } : {}) }));
      });

      socket.addEventListener('message', (event) => {
        if (disposed) return;
        let message: ServerMessage;
        try {
          message = JSON.parse(String(event.data)) as ServerMessage;
        } catch {
          return;
        }

        switch (message.type) {
          case 'welcome':
            seatToken.set(roomId, message.token);
            setRoom(message.room);
            setStatus('live');
            setFatal(null);
            break;
          case 'state':
            setRoom(message.room);
            setStatus('live');
            break;
          case 'error':
            if (FATAL_CODES.has(message.code)) {
              // A stale token for a room the server forgot should not lock us out.
              if (message.code === 'room_not_found') seatToken.clear(roomId);
              closedByUsRef.current = true;
              setFatal(message.message);
              setStatus('rejected');
              socket.close();
            } else {
              setNotice(message.message);
            }
            break;
          case 'pong':
            break;
        }
      });

      socket.addEventListener('close', () => {
        if (disposed || closedByUsRef.current) return;

        const delay = RECONNECT_DELAYS_MS[Math.min(attemptRef.current, RECONNECT_DELAYS_MS.length - 1)] ?? 8_000;
        attemptRef.current += 1;
        setStatus('reconnecting');
        clearRetry();
        retryTimerRef.current = window.setTimeout(connect, delay);
      });
    };

    setStatus('connecting');
    connect();

    return () => {
      disposed = true;
      clearRetry();
      socketRef.current?.close();
      socketRef.current = null;
    };
    // Reconnect only when the seat itself changes, not on every render.
  }, [credentials, roomId, name, role]);

  useEffect(() => {
    if (notice === null) return;
    const timer = window.setTimeout(() => setNotice(null), NOTICE_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const send = useCallback((command: ClientCommand) => {
    const socket = socketRef.current;
    if (socket?.readyState !== WebSocket.OPEN) {
      setNotice('Still reconnecting — try that again in a moment.');
      return;
    }
    socket.send(JSON.stringify({ type: 'command', command }));
  }, []);

  const leave = useCallback(() => {
    closedByUsRef.current = true;
    seatToken.clear(roomId);
    const socket = socketRef.current;
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: 'command', command: { type: 'leave' } }));
    }
    socket?.close();
  }, [roomId]);

  const dismissNotice = useCallback(() => setNotice(null), []);

  return useMemo(
    () => ({ status, room, notice, fatal, send, leave, dismissNotice }),
    [status, room, notice, fatal, send, leave, dismissNotice],
  );
}
